/**
 * AdminPage — Token management and platform statistics for admins.
 *
 * States: disconnected (connect wallet prompt), non-admin (403 forbidden),
 * loading (skeleton), error (retry), success (stats + token table + dialog).
 *
 * Story 7.3 — Task 6
 */

import type { Token, VerificationStatus } from "@repo/shared-types";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AdminStatsPanel } from "../components/admin/AdminStatsPanel";
import { AdminTokenTable } from "../components/admin/AdminTokenTable";
import { VerificationDialog } from "../components/admin/VerificationDialog";
import { useAdminStats } from "../hooks/api/use-admin-stats";
import { type AdminTokenFilters, useAdminTokens } from "../hooks/api/use-admin-tokens";
import { useVerifyToken } from "../hooks/api/use-verify-token";
import { useWallet } from "../hooks/use-wallet";
import { fadeInUp } from "../lib/animations";

type DialogState = {
	open: boolean;
	token: Token | null;
	action: "verify" | "flag" | "remove";
};

const ACTION_TO_STATUS: Record<string, VerificationStatus> = {
	verify: "verified",
	flag: "flagged",
	remove: "unverified",
};

export function AdminPage() {
	const { isConnected, login } = useWallet();
	const [search, setSearch] = useState("");
	const [verification, setVerification] = useState("");
	const [cursor, setCursor] = useState<string | undefined>(undefined);
	const [accumulatedTokens, setAccumulatedTokens] = useState<Token[]>([]);
	const [toastMessage, setToastMessage] = useState<string | null>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const filters: AdminTokenFilters = useMemo(() => {
		const f: AdminTokenFilters = {};
		if (search) f.search = search;
		if (verification) f.verification = verification;
		if (cursor) f.cursor = cursor;
		return f;
	}, [search, verification, cursor]);

	const statsQuery = useAdminStats();
	const tokensQuery = useAdminTokens(filters);

	// Reset accumulated tokens when filters change (not cursor)
	const filterKey = `${search}|${verification}`;
	const prevFilterKeyRef = useRef(filterKey);
	useEffect(() => {
		if (prevFilterKeyRef.current !== filterKey) {
			setAccumulatedTokens([]);
			setCursor(undefined);
			prevFilterKeyRef.current = filterKey;
		}
	}, [filterKey]);

	// Append new tokens when data arrives
	useEffect(() => {
		if (tokensQuery.data?.tokens) {
			if (cursor) {
				setAccumulatedTokens((prev) => [...prev, ...tokensQuery.data.tokens]);
			} else {
				setAccumulatedTokens(tokensQuery.data.tokens);
			}
		}
	}, [tokensQuery.data, cursor]);
	const { mutate, reset, isPending, error: mutationError } = useVerifyToken();

	const [dialog, setDialog] = useState<DialogState>({
		open: false,
		token: null,
		action: "verify",
	});

	// Clean up toast timer on unmount (M7)
	useEffect(() => {
		return () => {
			if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
		};
	}, []);

	const showToast = useCallback((message: string) => {
		if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
		setToastMessage(message);
		toastTimerRef.current = setTimeout(() => setToastMessage(null), 3000);
	}, []);

	// H5: Extract stable references; M8: reset before dialog open
	const handleVerifyAction = useCallback(
		(tokenId: string, action: "verify" | "flag" | "remove") => {
			const token = tokensQuery.data?.tokens.find((t) => t.id === tokenId);
			if (!token) return;
			reset();
			setDialog({ open: true, token, action });
		},
		[tokensQuery.data, reset]
	);

	const handleConfirm = useCallback(
		(reason?: string) => {
			if (!dialog.token) return;
			const status = ACTION_TO_STATUS[dialog.action];
			mutate(
				{ tokenId: dialog.token.id, status, reason },
				{
					onSuccess: () => {
						setDialog({ open: false, token: null, action: "verify" });
						showToast(
							`Token ${dialog.action === "remove" ? "reset" : `${dialog.action}d`} successfully`
						);
					},
				}
			);
		},
		[dialog, mutate, showToast]
	);

	const handleCancelDialog = useCallback(() => {
		setDialog({ open: false, token: null, action: "verify" });
		reset();
	}, [reset]);

	// Disconnected state
	if (!isConnected) {
		return (
			<motion.div
				variants={fadeInUp}
				initial="hidden"
				animate="visible"
				className="mx-auto max-w-7xl px-4 py-16 text-center"
			>
				<h1 className="text-3xl font-bold text-pastel-black">Admin Panel</h1>
				<p className="mt-4 text-pastel-gray">Connect your wallet to access admin features.</p>
				<button
					type="button"
					onClick={login}
					className="mt-6 rounded-lg bg-pastel-vibrant-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
				>
					Connect Wallet
				</button>
			</motion.div>
		);
	}

	// Non-admin detection (403) — M1: check for status code pattern
	const isForbidden =
		statsQuery.error?.message?.includes("403") || statsQuery.error?.message?.includes("Forbidden");

	if (isForbidden) {
		return (
			<motion.div
				variants={fadeInUp}
				initial="hidden"
				animate="visible"
				className="mx-auto max-w-7xl px-4 py-16 text-center"
			>
				<h1 className="text-3xl font-bold text-pastel-black">Admin Access Required</h1>
				<p className="mt-4 text-pastel-gray">Your wallet does not have admin privileges.</p>
				<a
					href="/"
					className="mt-6 inline-block rounded-lg bg-pastel-vibrant-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
				>
					Back to Home
				</a>
			</motion.div>
		);
	}

	// Error state (non-403)
	const hasError = statsQuery.error && !isForbidden;
	if (hasError && !statsQuery.isLoading) {
		return (
			<motion.div
				variants={fadeInUp}
				initial="hidden"
				animate="visible"
				className="mx-auto max-w-7xl px-4 py-16 text-center"
			>
				<h1 className="text-3xl font-bold text-pastel-black">Unable to Load Admin Panel</h1>
				<p className="mt-4 text-pastel-gray">Something went wrong. Please try again.</p>
				<button
					type="button"
					onClick={() => {
						void statsQuery.refetch();
						void tokensQuery.refetch();
					}}
					className="mt-6 rounded-lg bg-pastel-vibrant-blue px-6 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
				>
					Retry
				</button>
			</motion.div>
		);
	}

	return (
		<motion.div
			variants={fadeInUp}
			initial="hidden"
			animate="visible"
			className="mx-auto max-w-7xl px-4 py-8"
		>
			<div className="mb-8">
				<h1 className="text-3xl font-bold text-pastel-black">Admin Panel</h1>
				<p className="mt-2 text-pastel-gray">Manage tokens and monitor platform health.</p>
			</div>

			{/* Platform Stats */}
			<div className="mb-8">
				<h2 className="mb-4 text-lg font-semibold text-pastel-black">Platform Stats</h2>
				<AdminStatsPanel stats={statsQuery.data} isLoading={statsQuery.isLoading} />
			</div>

			{/* Token Management */}
			<div>
				<h2 className="mb-4 text-lg font-semibold text-pastel-black">Token Management</h2>
				<AdminTokenTable
					tokens={accumulatedTokens}
					isLoading={tokensQuery.isLoading}
					hasMore={tokensQuery.data?.hasMore ?? false}
					onLoadMore={() => {
						const lastToken = accumulatedTokens[accumulatedTokens.length - 1];
						if (lastToken) {
							setCursor(lastToken.id);
						}
					}}
					onVerify={handleVerifyAction}
					search={search}
					onSearchChange={setSearch}
					verification={verification}
					onVerificationChange={setVerification}
				/>
			</div>

			{/* Verification Dialog */}
			<VerificationDialog
				open={dialog.open}
				token={dialog.token}
				action={dialog.action}
				isPending={isPending}
				error={mutationError}
				onConfirm={handleConfirm}
				onCancel={handleCancelDialog}
			/>

			{/* Toast */}
			{toastMessage && (
				<div className="fixed bottom-6 right-6 z-50 rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white shadow-lg">
					{toastMessage}
				</div>
			)}
		</motion.div>
	);
}
